import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { IconToastError } from '../../../../../../assets/toast-icons/icon-toast-error';
import { IconToastSuccess } from '../../../../../../assets/toast-icons/icon-toast-success';
import { Header } from '../../../../../../components/header';
import { HoursRangeFront } from '../../../channel-details/optimizer/configuration/types';
import { TariffName } from '../../../channel-details/optimizer/configuration/types/tariff-kind';
import { getLegendParams } from '../../../dashboard/utils/prices';
import { ChartPie } from '../components/chart-pie';
import { HeatmapLegend } from '../components/heatmap-legend';
import { groupRangesByTimeZoneKind } from '../utils';

type ChartTariffProps = {
  tariffName: string;
  hoursRanges: HoursRangeFront[];
  wholeWeekendCheaper: boolean;
  wholeHolidaysCheaper: boolean;
  ignoreSummerTimeChanges: boolean;
};

export const ChartTariff: React.FC<ChartTariffProps> = ({
  tariffName,
  hoursRanges,
  wholeHolidaysCheaper,
  wholeWeekendCheaper,
  ignoreSummerTimeChanges,
}) => {
  const { t } = useTranslation('channel-details');
  const groups = groupRangesByTimeZoneKind(hoursRanges);
  const timeZones = Object.keys(groups);

  const renderBooleanItem = (value: boolean, translateKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
      {value ? <IconToastSuccess /> : <IconToastError />}
      <span style={{ marginLeft: '12px' }}>{t(`optimizer.configuration.tariff.${translateKey}`)}</span>
    </Box>
  );

  const legendParams = getLegendParams(tariffName as TariffName);

  return (
    <>
      <HeatmapLegend {...(Array.isArray(legendParams) && { ranges: legendParams })} />
      <Box sx={{ backgroundColor: 'var(--color-element)', padding: 3, borderRadius: '8px' }}>
        <Stack
          direction={{ sm: 'row' }}
          sx={{
            width: '100%',
            display: 'flex',
            borderRadius: '8px',
          }}
        >
          {timeZones.length === 1 && <ChartPie ranges={groups[timeZones[0]]} key={timeZones[0]} />}

          {timeZones.length > 1 &&
            timeZones.map((tz) => (
              <div key={tz} style={{ marginBottom: '24px', width: '100%' }}>
                <Header
                  title={
                    tz === 'ALL'
                      ? 'Cały rok'
                      : t(`optimizer.configuration.tariff.${tz.toLowerCase()}Time`).toUpperCase()
                  }
                  center
                />
                <ChartPie ranges={groups[tz]} />
              </div>
            ))}
        </Stack>
        <Box>
          {renderBooleanItem(wholeWeekendCheaper, 'wholeWeekendCheaper')}
          {renderBooleanItem(wholeHolidaysCheaper, 'wholeHolidaysCheaper')}
          {renderBooleanItem(ignoreSummerTimeChanges, 'ignoreSummerTimeChanges')}
        </Box>
      </Box>
    </>
  );
};
